import type { TagView } from '@vizit/api';
import { Badge, BadgeLevel, BadgeShape, BadgeSize } from '@vizit/atoms';
import { Button, ButtonLevel, ButtonSize, Cell, Row, Skeleton, TableHeader } from '@vizit/atoms';
import { Menu, MenuItem, MenuPopover, MenuSeparator, MenuTrigger } from '@vizit/atoms';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { DotsHorizontalIcon, Edit03Icon, PlusIcon, Trash01Icon } from '@vizit/icons';
import { Heading, HeadingVariant, LibraryEmptyState } from '@vizit/molecules';
import { cva, cx } from '@vizit/theme';
import { formatDate } from '@vizit/utils';
import * as React from 'react';
import { Table, TableBody } from 'react-aria-components';
import { SearchTextField } from '../search-textfield';
import { DeleteTagDialog } from './delete-tag-dialog';
import { TagNameDialog } from './tag-name-dialog';

const tableContainerClasses = cva({
  base: 'w-full overflow-auto border border-secondary',
});

const rowClasses = cva({
  base: [
    'bg-white transition-all duration-150',
    'focus-visible:outline-none',
    '[&[data-focus-visible]]:outline-2 [&[data-focus-visible]]:outline [&[data-focus-visible]]:outline-purple-500 [&[data-focus-visible]]:outline-offset-[-2px]',
    'hover:bg-primary-hover',
  ],
});

const SKELETON_ROW_COUNT = 5;

type DialogState = { mode: 'create'; name?: string } | { mode: 'rename'; tag: TagView };

interface TagManagerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The tags to list.
   *
   * @required
   * @type {TagView[]}
   */
  tags: TagView[];
  /**
   * The number of tags that match the current search (every tag in the workspace when not searching), shown in the
   * header badge. When it is larger than `tags.length`, a notice says only part of the list is shown.
   *
   * @required
   * @type {number}
   */
  totalCount: number;
  /**
   * The current (debounced) search query, used by the empty state.
   *
   * @optional
   * @type {string}
   */
  search?: string;
  /**
   * Whether the tags are loading. Shows skeleton rows and disables creating tags.
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isLoading?: boolean;
  /**
   * Whether loading the tags failed. With no tags to show, an error with a retry replaces the empty states, so a
   * failed request is never mistaken for a workspace without tags.
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isError?: boolean;
  /**
   * Callback for the "Try again" button of the load error.
   *
   * @optional
   * @type {() => void}
   */
  onRetry?: () => void;
  /**
   * Callback when the search input changes (debounced by the search field).
   *
   * @optional
   * @type {(search: string) => void}
   */
  onSearchChange?: (search: string) => void;
  /**
   * Creates a tag. Resolve to close the dialog. Reject with the API error to keep it open and show the matching
   * message under the field (see `getTagErrorMessage`); any other error shows a generic message.
   *
   * @required
   * @type {(name: string) => Promise<void>}
   */
  onCreate: (name: string) => Promise<void>;
  /**
   * Renames a tag, with the same resolve and reject behavior as `onCreate`. It is not called when the new name
   * equals the current one.
   *
   * @required
   * @type {(tag: TagView, name: string) => Promise<void>}
   */
  onRename: (tag: TagView, name: string) => Promise<void>;
  /**
   * Deletes a tag after the user confirms. Resolve to close the dialog. Reject to keep it open for a retry. The
   * caller owns the error UI (for example a toast): the dialog shows nothing itself.
   *
   * @required
   * @type {(tag: TagView) => Promise<void>}
   */
  onDelete: (tag: TagView) => Promise<void>;
}

/**
 * The workspace Tag Manager: a searchable list of tags with create, rename and delete.
 *
 * @param props - The props for the TagManager component.
 */
function TagManager(props: TagManagerProps) {
  const {
    tags,
    totalCount,
    search = '',
    isLoading = false,
    isError = false,
    onRetry,
    onSearchChange,
    onCreate,
    onRename,
    onDelete,
    className,
    ...other
  } = props;

  const [dialog, setDialog] = React.useState<DialogState | null>(null);
  const [tagToDelete, setTagToDelete] = React.useState<TagView | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

  const sortedTags = React.useMemo(() => [...tags].sort((a, b) => a.name.localeCompare(b.name)), [tags]);

  const closeNameDialog = (open: boolean) => {
    if (!open) setDialog(null);
  };

  const openDeleteDialog = (tag: TagView) => {
    setTagToDelete(tag);
    setIsDeleteOpen(true);
  };

  // The tag stays set while the dialog closes, so its title and image count don't change during the exit transition.
  const closeDeleteDialog = (open: boolean) => {
    if (!open) setIsDeleteOpen(false);
  };

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => {
        const skeletonId = `skeleton-row-${index}`;
        return (
          <Row key={skeletonId} id={skeletonId} aria-label="Loading tag row">
            <Cell className="py-8 px-12">
              <Skeleton className="h-10 w-48" variant="square" />
            </Cell>
            <Cell className="py-8 px-12 w-auto">
              <Skeleton className="h-10 w-12" variant="square" />
            </Cell>
            <Cell className="py-8 px-12 w-auto">
              <Skeleton className="h-10 w-24" variant="square" />
            </Cell>
            <Cell className="py-8 px-12 w-auto">
              <Skeleton className="h-8 w-8" variant="rounded" />
            </Cell>
          </Row>
        );
      });
    }

    return sortedTags.map((tag) => (
      <Row key={tag.id} id={tag.id} className={rowClasses()} aria-label={`Tag: ${tag.name}`}>
        <Cell className="py-8 px-12">
          <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary} maxLines={1} showEllipsis>
            {tag.name}
          </Text>
        </Cell>
        <Cell className="py-8 px-12 w-auto">
          <Text size={TextSize.Sm} color={TextColor.Secondary}>
            {tag.image_count.toLocaleString()}
          </Text>
        </Cell>
        <Cell className="py-8 px-12 w-auto">
          <Text size={TextSize.Sm} color={TextColor.Secondary}>
            {formatDate(new Date(tag.created_at))}
          </Text>
        </Cell>
        <Cell className="py-8 px-12 w-auto">
          <MenuTrigger>
            <Button level={ButtonLevel.Ghost} size={ButtonSize.Sm} aria-label={`Actions for ${tag.name}`}>
              <DotsHorizontalIcon size={16} className="text-gray-utility-400" />
            </Button>
            <MenuPopover>
              <Menu>
                <MenuItem onAction={() => setDialog({ mode: 'rename', tag })}>
                  <Edit03Icon size={16} />
                  Edit tag
                </MenuItem>
                <MenuSeparator />
                <MenuItem onAction={() => openDeleteDialog(tag)}>
                  <Trash01Icon size={16} />
                  Delete tag
                </MenuItem>
              </Menu>
            </MenuPopover>
          </MenuTrigger>
        </Cell>
      </Row>
    ));
  };

  const hasLoadError = isError && !isLoading && sortedTags.length === 0;
  const isEmpty = !isLoading && !hasLoadError && sortedTags.length === 0;

  return (
    <div className={cx('flex flex-col gap-8 w-full', className)} {...other}>
      <div className="flex items-end justify-between w-full gap-6 max-sm:flex-col max-sm:items-start">
        <div className="flex flex-col gap-1">
          <div className="flex gap-4 items-center">
            <Heading variant={HeadingVariant.H4}>Tags</Heading>
            <Badge shape={BadgeShape.Box} level={BadgeLevel.Brand} size={BadgeSize.Sm}>
              {totalCount}
            </Badge>
          </div>
          <Text size={TextSize.Sm} color={TextColor.Tertiary}>
            Create, organize, and clean up tags used across your catalog.
          </Text>
        </div>
        <div className="flex gap-6 items-center">
          <SearchTextField
            className="w-[320px]"
            placeholder="Search tags"
            debounceDelay={300}
            onSearch={onSearchChange}
            variant="filled"
          />
          <Button
            level={ButtonLevel.Primary}
            size={ButtonSize.Sm}
            iconStart={<PlusIcon size={16} />}
            isDisabled={isLoading}
            onPress={() => setDialog({ mode: 'create' })}
          >
            New tag
          </Button>
        </div>
      </div>

      {hasLoadError ? (
        <LibraryEmptyState
          heading="We couldn't load your tags."
          description="Check your connection and try again."
          icon={Edit03Icon}
        >
          <Button level={ButtonLevel.Primary} size={ButtonSize.Sm} onPress={onRetry}>
            Try again
          </Button>
        </LibraryEmptyState>
      ) : isEmpty ? (
        search ? (
          <LibraryEmptyState
            heading={`No tags match "${search}".`}
            description="Try a different search, or create a new tag with this name."
            icon={Edit03Icon}
          >
            <Button
              level={ButtonLevel.Primary}
              size={ButtonSize.Sm}
              iconStart={<PlusIcon size={16} />}
              onPress={() => setDialog({ mode: 'create', name: search })}
            >
              {`Create tag "${search}"`}
            </Button>
          </LibraryEmptyState>
        ) : (
          <LibraryEmptyState
            heading="No tags yet."
            description="Tags are how you organize assets across the catalog."
            icon={Edit03Icon}
          >
            <Button
              level={ButtonLevel.Primary}
              size={ButtonSize.Sm}
              iconStart={<PlusIcon size={16} />}
              onPress={() => setDialog({ mode: 'create' })}
            >
              Create your first tag
            </Button>
          </LibraryEmptyState>
        )
      ) : (
        <div className="flex flex-col gap-4">
          <div className={tableContainerClasses()}>
            <Table className="w-full" aria-label="Tags table">
              <TableHeader>
                <TableHeader.Column isRowHeader style={{ width: '50%' }}>
                  Tag
                </TableHeader.Column>
                <TableHeader.Column className="w-auto">Images</TableHeader.Column>
                <TableHeader.Column className="w-auto">Created</TableHeader.Column>
                <TableHeader.Column aria-label="Actions" className="w-auto">
                  {' '}
                </TableHeader.Column>
              </TableHeader>
              <TableBody>{renderBody()}</TableBody>
            </Table>
          </div>
          {!isLoading && totalCount > sortedTags.length && (
            <Text size={TextSize.Sm} color={TextColor.Tertiary}>
              {`Showing ${sortedTags.length.toLocaleString()} of ${totalCount.toLocaleString()} tags. Search to find the rest.`}
            </Text>
          )}
        </div>
      )}

      {dialog && (
        <TagNameDialog
          key={dialog.mode === 'rename' ? dialog.tag.id : `create:${dialog.name ?? ''}`}
          isOpen
          onOpenChange={closeNameDialog}
          tag={dialog.mode === 'rename' ? dialog.tag : undefined}
          defaultName={dialog.mode === 'create' ? dialog.name : undefined}
          onSubmit={(name) => (dialog.mode === 'rename' ? onRename(dialog.tag, name) : onCreate(name))}
        />
      )}
      <DeleteTagDialog isOpen={isDeleteOpen} onOpenChange={closeDeleteDialog} tag={tagToDelete} onDelete={onDelete} />
    </div>
  );
}

export { TagManager };
export type { TagManagerProps };
